import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
} from '@angular/forms';
import { get } from 'lodash';
import { rx, Observable } from '@shared/rx';

import { IWidgetMappers } from './schema-form-interfaces';

export const memoizeLatest = (mapperFunction: (input: any) => any): any => {
  if (!mapperFunction) {
    return null;
  }
  let memoizedInput = Symbol();
  let memoizedOutput;

  return (inputValue: any): any => {
    if (inputValue !== memoizedInput) {
      memoizedInput = inputValue;
      memoizedOutput = mapperFunction(inputValue);
    }

    return memoizedOutput;
  };
};

/**
 * proxifyWidgetControl
 * returns formControl proxy which have all value related properties mapped with provided mappers.
 * When no mappers have been provided return unchanged control
 */
export const createWidgetControlProxy = <T extends AbstractControl>(
  formControl: T,
  mappers: IWidgetMappers,
): T => {
  const cachedFormToWidgetMapper = memoizeLatest(
    get(mappers, 'formToWidgetMapper'),
  );
  const cachedWidgetToFormMapper = memoizeLatest(
    get(mappers, 'widgetToFormMapper'),
  );

  if (
    !formControl ||
    (!cachedFormToWidgetMapper && !cachedWidgetToFormMapper)
  ) {
    return formControl;
  }

  const redefinedProps: {
    -readonly [key in
      | keyof FormControl
      | keyof FormGroup
      | keyof FormArray]?: PropertyDescriptor;
  } = {};

  if (cachedFormToWidgetMapper) {
    redefinedProps.value = {
      get(): any {
        // eslint-disable-next-line no-invalid-this
        return cachedFormToWidgetMapper(Object.getPrototypeOf(this).value);
      },
    };
    redefinedProps.valueChanges = {
      // Notice: original .valueChanges is a EventEmitter<any>. The replacement breaks .updateValueAndValidity().
      get(): Observable<any> {
        // eslint-disable-next-line no-invalid-this
        return Object.getPrototypeOf(this).valueChanges.pipe(
          rx.map(cachedFormToWidgetMapper),
          rx.share(),
        );
      },
    };
  }

  if (cachedWidgetToFormMapper) {
    ['setValue', 'patchValue', 'reset'].forEach((propertyName) => {
      redefinedProps[propertyName] = {
        get(): (value: any, options?: Object) => void {
          return (value, options?): void =>
            // eslint-disable-next-line no-invalid-this
            Object.getPrototypeOf(this)[propertyName](
              cachedWidgetToFormMapper(value),
              options,
            );
        },
      };
    });
  }

  const methods = ['markAsDirty', 'markAsPristine'];

  methods.forEach((methodName) => {
    redefinedProps[methodName] = {
      get(): Observable<any> {
        return formControl[methodName].bind(formControl);
      },
    };
  });

  const props = ['dirty', 'pristine', 'pending', 'invalid', 'valid'];

  props.forEach((propertyName) => {
    redefinedProps[propertyName] = {
      get(): Observable<any> {
        return formControl[propertyName];
      },
    };
  });

  redefinedProps.errors = {
    get(): ValidationErrors | null {
      return formControl.errors;
    },
    set(value): void {
      formControl.setErrors(value);
    },
  };

  if (formControl instanceof FormControl) {
    redefinedProps.registerOnChange = {
      value: (fn: (newValue: any, emitModelEvent: boolean) => void): void => {
        formControl.registerOnChange((newValue: any, emitModelEvent: boolean) =>
          fn(cachedFormToWidgetMapper(newValue), emitModelEvent),
        );
      },
    };
  }

  const proxyControl = Object.create(formControl, redefinedProps);

  return proxyControl;
};
